import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  Shield, ArrowRight, Zap, Eye, Activity,
  Lock, Server, Cpu,
  Database, Network, Search, ArrowUpRight,
  Users, CheckCircle2, Globe
} from 'lucide-react';
import './Home.css';

/* ============================================================
   DATA
   ============================================================ */

const services = [
  {
    id: '01',
    icon: <Shield size={32} />,
    title: 'AI 托管安全运营',
    desc: '基于 TitanSIEM 大模型分析引擎，将海量告警降噪压缩，平均威胁发现时间 < 15 分钟，提供企业级智能防护。',
    path: '/services/security-ops',
    color: '#0a66c2',
    features: ['大模型智能研判', '7×24 自动化响应', 'MTTD < 15m']
  },
  {
    id: '02',
    icon: <Eye size={32} />,
    title: '深度渗透测试',
    desc: '模拟实战 APT 攻击链路，深度挖掘 Web、APP 及内网隐藏漏洞。',
    path: '/services/pen-test',
    color: '#5b21b6',
    features: ['APT 攻击模拟', '0day 漏洞挖掘', '修复路线图']
  },
  {
    id: '03',
    icon: <Zap size={32} />,
    title: '极速应急响应',
    desc: '15 分钟内建立远程作战室，提供溯源、取证、恢复三合一服务。',
    path: '/services/emergency-response',
    color: '#d97706',
    features: ['15m 远程响应', '攻击溯源', '业务快速恢复']
  },
  {
    id: '04',
    icon: <Activity size={32} />,
    title: '持续漏洞扫描',
    desc: '覆盖全量 IT/OT 资产，基于最新漏洞库提供持续性订阅扫描。',
    path: '/services/vuln-scan',
    color: '#059669',
    features: ['全资产覆盖', '持续性订阅', '双库融合引擎']
  },
];

const coreCapabilities = [
  {
    icon: <Network size={24} />,
    title: '大模型全流量研判',
    desc: 'TB级网络流量深度解析，通过安全大语言模型精准识别 0day 漏洞利用及变种隐蔽威胁。'
  },
  {
    icon: <Database size={24} />,
    title: 'AI 驱动异构数据湖',
    desc: '统一汇聚各类安全日志，利用大模型构建自动化标签与标准化数据模型，打破信息孤岛。'
  },
  {
    icon: <Search size={24} />,
    title: '智能自动化威胁狩猎',
    desc: '结合专家攻防知识图谱与大模型推理能力，自动生成狩猎剧本，高效还原完整攻击链。'
  }
];

const metrics = [
  { value: '15+', label: '年安全行业经验' },
  { value: '300+', label: '服务中小型政企' },
  { value: '99.9%', label: '平台可用性保障' },
  { value: '50亿+', label: '日均处理安全事件' }
];

const MOCK_LOGS = [
  { text: "INITIALIZING_LLM_ANALYSIS_ENGINE...", type: "info" },
  { text: "INGESTING_TRAFFIC_FROM_NODE_ALPHA", type: "info" },
  { text: "EXTRACTING_PAYLOAD_SEMANTICS...", type: "info" },
  { text: "LLM_DETECTED_ANOMALOUS_API_PATTERN", type: "warn" },
  { text: "THREAT_SCORE: 92.8 (HIGH_CONFIDENCE)", type: "warn" },
  { text: "GENERATING_MITIGATION_SCRIPT...", type: "info" },
  { text: "APPLYING_ZERO_TRUST_ISOLATION_RULES", type: "warn" },
  { text: "ISOLATION_SUCCESSFUL (LATENCY: 12ms)", type: "success" },
  { text: "GENERATED_INCIDENT_REPORT_VIA_LLM", type: "success" },
  { text: "UPDATING_GLOBAL_THREAT_MODELS...", type: "info" },
  { text: "SYSTEM_STATE_SECURE_AND_LEARNING", type: "success" }
];

/* ============================================================
   SUB-COMPONENTS
   ============================================================ */

function AbstractVisual() {
  return (
    <div className="hero-visual-wrapper">
      <div className="hero-visual-abstract">
        {/* 中心核心 */}
        <motion.div 
          className="core-shield"
          animate={{ 
            boxShadow: ['0 0 0 0 rgba(10, 102, 194, 0.2)', '0 0 0 40px rgba(10, 102, 194, 0)'],
          }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Shield size={48} color="#0a66c2" strokeWidth={1.5} />
        </motion.div>

        {/* 环绕轨道 */}
        <div className="orbit orbit-1">
          <motion.div 
            className="orbit-node"
            animate={{ rotate: 360 }}
            transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
          >
            <div className="node-icon"><Lock size={16} /></div>
          </motion.div>
        </div>
        
        <div className="orbit orbit-2">
          <motion.div 
            className="orbit-node"
            animate={{ rotate: -360 }}
            transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
          >
            <div className="node-icon"><Server size={16} /></div>
          </motion.div>
        </div>

        <div className="orbit orbit-3">
          <motion.div 
            className="orbit-node"
            animate={{ rotate: 360 }}
            transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
          >
            <div className="node-icon"><Cpu size={16} /></div>
          </motion.div>
        </div>

        {/* 数据流连线 */}
        <svg className="data-lines" viewBox="0 0 400 400">
          <motion.circle 
            cx="200" cy="200" r="120" 
            fill="none" 
            stroke="rgba(10, 102, 194, 0.1)" 
            strokeWidth="1"
            strokeDasharray="4 8"
          />
          <motion.circle 
            cx="200" cy="200" r="160" 
            fill="none" 
            stroke="rgba(10, 102, 194, 0.05)" 
            strokeWidth="1"
            strokeDasharray="2 6"
          />
        </svg>
      </div>
    </div>
  );
}

function CodeTerminal() {
  const [logs, setLogs] = useState([]);
  
  useEffect(() => {
    let index = 0;
    // 初始不放数据，从头开始打印
    setLogs([]);

    const interval = setInterval(() => {
      setLogs(prev => {
        // 每次增加一条新日志
        const newLogs = [...prev, MOCK_LOGS[index]];
        // 限制最大行数，多余的在上方消失，形成真实的向上滚动感
        if (newLogs.length > 15) {
          newLogs.shift();
        }
        return newLogs;
      });
      
      index++;
      if (index >= MOCK_LOGS.length) {
        index = 0; // 循环播放
      }
    }, 600); // 稍微加快刷新速度，更像真实终端

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="cap-code-overlay">
      <div className="terminal-header">
        <span className="dot dot-red"></span>
        <span className="dot dot-yellow"></span>
        <span className="dot dot-green"></span>
        <span className="terminal-title">bash — SecLLM_Inference_Node</span>
      </div>
      <div className="terminal-scroll-area">
        <div className="terminal-body">
          {logs.map((log, i) => (
            <div key={`${i}-${log.text}`} className={`code-line log-${log.type}`}>
              <span className="prompt">{log.type === 'warn' ? '!' : '>'}</span> 
              {log.text}
            </div>
          ))}
          <motion.div 
            className="code-line cursor-line"
            animate={{ opacity: [1, 0] }}
            transition={{ repeat: Infinity, duration: 0.8 }}
          >
            <span className="prompt">></span> <span className="cursor-block"></span>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MAIN PAGE
   ============================================================ */

export default function Home() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, 100]);
  const heroOpacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <div className="home">
      {/* ====== 1. HERO ====== */}
      <section className="hero">
        <div className="container hero-container">
          <div className="hero-content">
            <motion.div 
              className="hero-text-block"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="hero-eyebrow">
                <span className="live-dot" />
                下一代 AI 驱动安全运营架构
              </div>

              <h1 className="hero-title-main">
                大模型重塑<br />
                <span className="text-highlight">智能主动安全。</span>
              </h1>

              <p className="hero-description">
                基于自研安全大语言模型（SecLLM）与 TitanSIEM 引擎，
                将海量告警转化为极速阻断能力。提供写进合同的 MTTD &lt; 15 分钟智能托管体系。
              </p>

              <div className="hero-action-group">
                <Link to="/contact" className="btn-solid-primary">
                  获取安全评估 <ArrowRight size={18} />
                </Link>
                <Link to="/services/security-ops" className="btn-text-link">
                  了解 TitanSIEM 平台
                </Link>
              </div>

              <div className="hero-metrics">
                <div className="metric-item">
                  <strong>&lt; 15m</strong>
                  <span>平均威胁发现</span>
                </div>
                <div className="metric-divider" />
                <div className="metric-item">
                  <strong>15m</strong>
                  <span>远程应急响应</span>
                </div>
                <div className="metric-divider" />
                <div className="metric-item">
                  <strong>99.9%</strong>
                  <span>防护可靠性</span>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="hero-visual-block"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              style={{ y: heroY, opacity: heroOpacity }}
            >
              <AbstractVisual />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ====== 1.5. DATA METRICS ====== */}
      <section className="section-data-metrics">
        <div className="container">
          <div className="data-metrics-grid">
            {metrics.map((m, i) => (
              <motion.div 
                key={m.label}
                className="data-metric-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="metric-val">{m.value}</div>
                <div className="metric-lbl">{m.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== 2. BENTO GRID SERVICES ====== */}
      <section className="section-services bg-light">
        <div className="container">
          <div className="section-header-center">
            <h2 className="section-title-large">安全能力矩阵</h2>
            <p className="section-subtitle">覆盖企业全生命周期的网络安全服务体系，满足等保合规与实战攻防需求。</p>
          </div>

          <div className="bento-grid">
            {services.map((svc, i) => (
              <motion.div 
                key={svc.id}
                className={`bento-item bento-item-${i + 1}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
              >
                <Link to={svc.path} className="bento-link-wrapper">
                  <div className="bento-bg-gradient" style={{ '--bento-color': svc.color }} />
                  
                  <div className="bento-top">
                    <div className="bento-icon" style={{ color: svc.color }}>
                      {svc.icon}
                    </div>
                    <div className="bento-arrow">
                      <ArrowUpRight size={24} />
                    </div>
                  </div>

                  <div className="bento-content">
                    <h3 className="bento-title">{svc.title}</h3>
                    <p className="bento-desc">{svc.desc}</p>
                    
                    <div className="bento-features">
                      {svc.features.map(f => (
                        <span key={f} className="bento-feature-tag">{f}</span>
                      ))}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== 2.5. CORE CAPABILITIES ====== */}
      <section className="section-capabilities">
        <div className="container">
          <div className="capabilities-inner">
            <div className="cap-text-side">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="section-title-large">底层核心技术</h2>
                <p className="section-subtitle mb-48">
                  卓迈安全体系的基石，不仅是堆砌安全设备，而是通过大语言模型（LLM）的推理能力与深度算法创新，赋予安全运营真正的“智慧大脑”。
                </p>
                
                <div className="cap-list">
                  {coreCapabilities.map((cap, i) => (
                    <div key={cap.title} className="cap-item">
                      <div className="cap-icon">{cap.icon}</div>
                      <div className="cap-info">
                        <h4>{cap.title}</h4>
                        <p>{cap.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
            
            <div className="cap-visual-side">
                 <CodeTerminal />
            </div>
          </div>
        </div>
      </section>

      {/* ====== 2.8. WHY CHOOSE US (New) ====== */}
      <section className="section-why-us bg-light">
        <div className="container">
          <div className="section-header-center">
            <h2 className="section-title-large">为什么选择卓迈科技</h2>
            <p className="section-subtitle">不仅是工具的提供者，更是安全体系的建设者</p>
          </div>

          <div className="why-grid">
            {[
              { icon: <CheckCircle2 size={28} />, title: "极速响应服务", desc: "专业团队在线，突发事件得到最快处置。" },
              { icon: <Globe size={28} />, title: "运营商级网络接入", desc: "联合三大运营商，拥有得天独厚的网络层数据获取与防御能力。" },
              { icon: <Users size={28} />, title: "白帽子攻防团队", desc: "由多名资深安全专家与一线实战白帽组成，深刻理解黑客攻击思路。" }
            ].map((item, i) => (
              <motion.div 
                key={item.title}
                className="why-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
              >
                <div className="why-icon">{item.icon}</div>
                <h3 className="why-title">{item.title}</h3>
                <p className="why-desc">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ====== 3. BOLD STATEMENT ====== */}
      <section className="section-statement">
        <div className="container">
          <motion.div 
            className="statement-content"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2>安全不应是少数企业的特权。</h2>
            <p>我们通过自研架构与标准化服务，让高级安全运营能力平民化。</p>
            <Link to="/contact" className="btn-solid-dark">
              联系我们的安全专家
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
